import { put, call, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { selectAudioQuery } from '../selectors/audio';
import { updateAudioTaxonomy } from '../apis/taxonomy';
import {
  fetchAudios,
  createAudio,
  updateAudio,
  deleteAudio,
  fetchAudioById,
  bulkDeleteAudios,
  bulkUpdateAudioAttributes,
} from '../apis/audio';
import {
  getAudiosAction,
  getAudiosSuccess,
  getAudiosFailure,
  createAudioAction,
  updateAudioAction,
  deleteAudioAction,
  createAudioSuccess,
  createAudioFailure,
  updateAudioSuccess,
  updateAudioFailure,
  deleteAudioSuccess,
  deleteAudioFailure,
  getAudioByIdAction,
  getAudioByIdSuccess,
  getAudioByIdFailure,
  bulkDeleteAudiosAction,
  bulkDeleteAudiosSuccess,
  bulkDeleteAudiosFailure,
  bulkUpdateAudioAttributesAction,
  bulkUpdateAudioAttributesSuccess,
  bulkUpdateAudioAttributesFailure,
} from '../slices/audio';

function* getAudios(action) {
  try {
    const response = yield call(fetchAudios, action.payload);
    yield put(getAudiosSuccess(response));
  } catch (error) {
    yield put(
      getAudiosFailure(
        error.response?.data?.error?.message || error.message || 'Failed to fetch audios'
      )
    );
  }
}

function* handleCreateAudio(action) {
  try {
    const { data, taxonomy } = action.payload;
    const created = yield call(createAudio, data);
    // Create then file: the audio serializers ignore node_ids/attributes on POST.
    const createdId = created?.data?.id || created?.id;
    if (createdId && taxonomy) yield call(updateAudioTaxonomy, createdId, taxonomy);
    // No list refetch here: the listing refetches on mount after the redirect.
    yield put(createAudioSuccess());
  } catch (error) {
    yield put(
      createAudioFailure(
        error.response?.data?.error?.message || error.message || 'Failed to create audio'
      )
    );
  }
}

function* handleUpdateAudio(action) {
  try {
    const { id, data, taxonomy } = action.payload;
    const response = yield call(updateAudio, id, data);
    // Chain the taxonomy PUT so success means both writes landed (no race).
    if (taxonomy) yield call(updateAudioTaxonomy, id, taxonomy);
    yield put(updateAudioSuccess(response));
  } catch (error) {
    yield put(
      updateAudioFailure(
        error.response?.data?.error?.message || error.message || 'Failed to update audio'
      )
    );
  }
}

// Refetch the page and filters the user is on now; they may have changed while the write ran.
function* refetchAudios() {
  yield put(getAudiosAction(yield select(selectAudioQuery)));
}

function* handleDeleteAudio(action) {
  try {
    const { id } = action.payload;
    yield call(deleteAudio, id);
    yield put(deleteAudioSuccess());
    yield call(refetchAudios);
  } catch (error) {
    yield put(
      deleteAudioFailure(
        error.response?.data?.error?.message || error.message || 'Failed to delete audio'
      )
    );
  }
}

function* handleBulkUpdateAudioAttributes(action) {
  try {
    const { ids, set, clear } = action.payload;
    yield call(bulkUpdateAudioAttributes, ids, set, clear);
    yield put(bulkUpdateAudioAttributesSuccess());
    yield call(refetchAudios);
  } catch (error) {
    yield put(
      bulkUpdateAudioAttributesFailure(
        error.response?.data?.error?.message || error.message || 'Failed to update attributes'
      )
    );
  }
}

function* handleBulkDeleteAudios(action) {
  try {
    const { ids } = action.payload;
    yield call(bulkDeleteAudios, ids);
    yield put(bulkDeleteAudiosSuccess());
    yield call(refetchAudios);
  } catch (error) {
    yield put(
      bulkDeleteAudiosFailure(
        error.response?.data?.error?.message || error.message || 'Failed to delete audios'
      )
    );
  }
}

function* getAudioById(action) {
  try {
    const response = yield call(fetchAudioById, action.payload);
    yield put(getAudioByIdSuccess(response));
  } catch (error) {
    yield put(
      getAudioByIdFailure(
        error.response?.data?.error?.message || error.message || 'Failed to fetch audio'
      )
    );
  }
}

export default function* audioSaga() {
  yield takeLatest(getAudiosAction.type, getAudios);
  yield takeLatest(getAudioByIdAction.type, getAudioById);
  yield takeEvery(createAudioAction.type, handleCreateAudio);
  yield takeEvery(updateAudioAction.type, handleUpdateAudio);
  yield takeEvery(deleteAudioAction.type, handleDeleteAudio);
  yield takeLatest(bulkUpdateAudioAttributesAction.type, handleBulkUpdateAudioAttributes);
  yield takeLatest(bulkDeleteAudiosAction.type, handleBulkDeleteAudios);
}
